/* Admin · Unified Webhook Events Explorer (P1 · Feb 2026)
 *
 * Single console for every inbound webhook across every provider:
 * TabaPay · Plaid · Middesk · Two.inc · Allianz · Verqo Shipping · WARP · Shopify
 * · Resend · Twilio. Normalises disparate row shapes into one unified view
 * with filter + replay + per-event detail drawer.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/PortalShell";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import {
  Webhook, Activity, AlertTriangle, CheckCircle2, XCircle, Clock,
  RefreshCw, Loader2, Eye, RotateCw, ShieldOff, ShieldCheck, ChevronRight,
} from "lucide-react";


function fmtAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const ago = (Date.now() - d.getTime()) / 1000;
    if (ago < 60) return "just now";
    if (ago < 3600) return `${Math.floor(ago / 60)}m ago`;
    if (ago < 86400) return `${Math.floor(ago / 3600)}h ago`;
    if (ago < 604800) return `${Math.floor(ago / 86400)}d ago`;
    return d.toLocaleString();
  } catch { return iso; }
}

const HEALTH_COLORS = {
  healthy:  "bg-emerald-50 border-emerald-200 text-emerald-900",
  warning:  "bg-amber-50 border-amber-200 text-amber-900",
  critical: "bg-rose-50 border-rose-200 text-rose-900",
  no_data:  "bg-slate-50 border-slate-200 text-slate-600",
};

const PROVIDER_LABELS = {
  tabapay: "TabaPay", plaid: "Plaid", middesk: "Middesk", two: "Two.inc",
  allianz: "Allianz", shippo: "Verqo Shipping", warp: "WARP", shopify: "Shopify",
  resend: "Resend", twilio: "Twilio",
};


export default function AdminWebhooks() {
  const [stats, setStats]       = useState(null);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [provider, setProvider] = useState("");
  const [sinceDays, setSinceDays] = useState(7);
  const [onlyBadSig, setOnlyBadSig] = useState(false);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        since_days: String(sinceDays),
        limit: "200",
      });
      if (provider)   params.set("provider", provider);
      if (onlyBadSig) params.set("only_bad_sig", "true");
      if (onlyFailed) params.set("only_failed", "true");
      const [statsR, listR] = await Promise.all([
        api.get(`/admin/webhooks/stats?since_days=${sinceDays}`),
        api.get(`/admin/webhooks/list?${params.toString()}`),
      ]);
      setStats(statsR.data);
      setData(listR.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load webhooks");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [provider, sinceDays, onlyBadSig, onlyFailed]);

  return (
    <div data-testid="admin-webhooks" className="space-y-6">
      <PageHeader
        title="Webhook Events Explorer"
        subtitle="Every inbound webhook across every provider · health · replay · forensic detail."
      />

      {/* Per-provider stats strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="wh-stats">
          {stats.stats.map((s) => (
            <button
              key={s.provider}
              data-testid={`wh-provider-${s.provider}`}
              onClick={() => setProvider(provider === s.provider ? "" : s.provider)}
              className={`rounded-xl border-2 p-3 text-left transition-colors ${
                provider === s.provider ? "ring-2 ring-slate-900" : ""
              } ${HEALTH_COLORS[s.health]}`}
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide opacity-70">
                <Webhook size={12}/> {s.label}
              </div>
              <div className="text-xl font-bold tabular-nums mt-1">{s.events}</div>
              <div className="text-[11px] mt-0.5 flex items-center gap-1.5">
                {s.health === "critical" && <AlertTriangle size={11}/>}
                {s.health === "healthy"  && <CheckCircle2 size={11}/>}
                {s.health === "warning"  && <AlertTriangle size={11}/>}
                {s.health === "no_data"  && <Clock size={11}/>}
                {s.bad_sig > 0 && <span>{s.bad_sig} bad-sig · </span>}
                {s.unapplied > 0 && <span>{s.unapplied} unapplied</span>}
                {s.bad_sig === 0 && s.unapplied === 0 && s.events > 0 && <span>all healthy</span>}
                {s.events === 0 && <span>no events</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-xl p-3" data-testid="wh-filters">
        <Select value={provider || "all"} onValueChange={(v) => setProvider(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="wh-filter-provider">
            <SelectValue placeholder="All providers"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All providers</SelectItem>
            {Object.entries(PROVIDER_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(sinceDays)} onValueChange={(v) => setSinceDays(Number(v))}>
          <SelectTrigger className="w-[120px] h-9 text-sm" data-testid="wh-filter-since">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">24h</SelectItem>
            <SelectItem value="7">7d</SelectItem>
            <SelectItem value="30">30d</SelectItem>
            <SelectItem value="90">90d</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-[12px] text-slate-700">
          <Checkbox checked={onlyBadSig} onCheckedChange={setOnlyBadSig} data-testid="wh-filter-badsig"/>
          Only bad signature
        </label>
        <label className="flex items-center gap-2 text-[12px] text-slate-700">
          <Checkbox checked={onlyFailed} onCheckedChange={setOnlyFailed} data-testid="wh-filter-failed"/>
          Only failed/unapplied
        </label>
        <Button data-testid="wh-refresh" variant="outline" size="sm" onClick={load} className="ml-auto">
          <RefreshCw size={14} className="mr-1.5"/> Refresh
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/> Loading webhooks…</div>
        ) : !data || data.rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="wh-empty">
            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2"/>
            No webhook events match your filters.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="wh-table">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Provider</th>
                <th className="text-left px-4 py-2 font-medium">Event</th>
                <th className="text-left px-4 py-2 font-medium">Summary</th>
                <th className="text-left px-4 py-2 font-medium">Sig</th>
                <th className="text-left px-4 py-2 font-medium">Applied</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.provider}-${r.id}-${i}`}
                    data-testid={`wh-row-${r.provider}-${r.event_id?.slice(0,12)}`}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSelected(r)}>
                  <td className="px-4 py-2 text-slate-600 tabular-nums text-[12px]">{fmtAt(r.received_at)}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px]">
                      {PROVIDER_LABELS[r.provider] || r.provider}
                    </span>
                  </td>
                  <td className="px-4 py-2 mono text-[11.5px] text-slate-700">{r.event_type}</td>
                  <td className="px-4 py-2 text-[12px] text-slate-600 max-w-[300px] truncate">{r.summary}</td>
                  <td className="px-4 py-2">
                    {r.signature_ok ? (
                      <ShieldCheck size={14} className="text-emerald-600"/>
                    ) : (
                      <ShieldOff size={14} className="text-rose-600"/>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {r.applied ? (
                      <CheckCircle2 size={14} className="text-emerald-600"/>
                    ) : (
                      <XCircle size={14} className="text-rose-600"/>
                    )}
                  </td>
                  <td className="px-2 py-2 text-slate-400"><ChevronRight size={14}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && <WebhookDetailDrawer row={selected} onClose={() => setSelected(null)} onRefresh={load}/>}
    </div>
  );
}


/* ─── Detail + Replay drawer ───────────────────────────────────────── */
function WebhookDetailDrawer({ row, onClose, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replayDialog, setReplayDialog] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/webhooks/${row.provider}/${row.id || row.event_id}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Could not load detail");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [row.id, row.event_id, row.provider]);

  return (
    <>
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="wh-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity size={16}/> {PROVIDER_LABELS[row.provider] || row.provider} · {row.event_type}
            </DialogTitle>
            <DialogDescription className="text-[12px] text-slate-500">
              Full raw payload + signature + apply state. Use Replay to re-fire through the original handler.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="p-12 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2"/> Loading…</div>
          ) : data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3" data-testid="wh-detail-summary">
                <Field label="Provider"      value={data.label}/>
                <Field label="Webhook URL"   value={data.url} mono/>
                <Field label="Event ID"      value={data.normalised.event_id} mono/>
                <Field label="Event type"    value={data.normalised.event_type} mono/>
                <Field label="Received at"   value={data.normalised.received_at}/>
                <Field label="Signature OK"  value={data.normalised.signature_ok ? "yes" : "NO"}/>
                <Field label="Applied"       value={data.normalised.applied ? "yes" : "NO"}/>
                {data.normalised.error && <Field label="Error" value={data.normalised.error}/>}
              </div>

              {data.raw?.replayed_at && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-[12px] text-amber-900" data-testid="wh-replayed-banner">
                  <div className="font-semibold">Previously replayed {fmtAt(data.raw.replayed_at)}</div>
                  <div>Status: {data.raw.replay_status_code} {data.raw.replay_error && `· ${data.raw.replay_error}`}</div>
                </div>
              )}

              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">Raw payload</div>
                <pre data-testid="wh-detail-raw"
                     className="bg-slate-900 text-emerald-200 text-[11px] p-3 rounded-xl overflow-x-auto max-h-[400px]">
                  {JSON.stringify(data.raw, null, 2)}
                </pre>
              </div>
            </div>
          )}

          <DialogFooter>
            {data?.raw?.raw && (                            /* must have a raw body to replay */
              <Button data-testid="wh-replay-btn" variant="outline" onClick={() => setReplayDialog(true)}>
                <RotateCw size={14} className="mr-1.5"/> Replay
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {replayDialog && (
        <ReplayDialog
          provider={row.provider}
          eventId={data?.normalised?.id || row.id || row.event_id}
          onClose={() => setReplayDialog(false)}
          onDone={() => { onRefresh && onRefresh(); onClose(); }}
        />
      )}
    </>
  );
}


function ReplayDialog({ provider, eventId, onClose, onDone }) {
  const [reason, setReason] = useState(`Manual replay from webhook explorer`);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/webhooks/${provider}/${eventId}/replay`, { reason });
      if (r.data.ok) {
        toast.success(`Replayed · status ${r.data.status_code}`);
      } else {
        toast.warning(`Replay returned status ${r.data.status_code || "—"} · ${r.data.error || "see detail"}`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replay failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="wh-replay-dialog">
        <DialogHeader>
          <DialogTitle>Replay webhook</DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            Re-fires the stored raw payload through the live <span className="mono">/api/webhooks/{provider}</span> handler.
            Audit-logged with X-Admin-Replay header.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="wh-replay-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for replay (≥3 chars)"
          rows={3}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="wh-replay-submit" onClick={submit} disabled={busy || reason.trim().length < 3}>
            {busy ? <Loader2 className="animate-spin mr-1.5" size={14}/> : <RotateCw size={14} className="mr-1.5"/>}
            Replay
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`${mono ? "mono" : ""} text-[13px] font-medium text-slate-800 break-all`}>
        {String(value || "—")}
      </div>
    </div>
  );
}
